import React, { useState, useEffect } from "react";
import { Typewriter } from "react-simple-typewriter";
import { FaGithub, FaDownload, FaLinkedin, FaCode, FaEnvelope } from "react-icons/fa";
import mypic from "../assets/mypic.jpeg";
import Navbar from "../navigation/navbar";
import { Blurhash } from "react-blurhash";

const techStacks = [
  { name: "JavaScript", color: "text-orange-400" },
  { name: "React", color: "text-orange-400" },
  { name: "Node.js", color: "text-orange-500" },
  { name: "MongoDB", color: "text-orange-400" },
  { name: "HTML5", color: "text-orange-500" },
  { name: "CSS3", color: "text-orange-400" },
  { name: "TypeScript", color: "text-orange-500" },
  { name: "Express", color: "text-orange-400" },
  { name: "Git", color: "text-orange-500" },
  { name: "Redux", color: "text-orange-400" },
  { name: "SQL", color: "text-orange-500" },
  { name: "API", color: "text-orange-400" }
];

// Tooltip component
const IconWithTooltip = ({ icon, label, href }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="relative">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {icon}
        {showTooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-black bg-opacity-80 text-white text-xs sm:text-sm rounded whitespace-nowrap">
            {label}
          </div>
        )}
      </a>
    </div>
  );
};

const HeroSection = () => {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setImageLoaded(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const hashCode = "LqJI65x]_Nt7%1RiV@t7t6WBRjoL";

  return (
    <section className="px-4 sm:px-6 md:px-10 pt-8 bg-black min-h-screen h-auto flex items-center relative overflow-hidden">
      <Navbar />

      {/* Floating Tech Stacks - Hidden on smaller screens */}
      <div className="absolute left-0 top-0 h-full w-1/3 overflow-hidden pointer-events-none z-0 hidden md:block">
        {techStacks.map((tech, index) => (
          <div
            key={tech.name}
            className={`animate-float${(index % 8) + 1} absolute ${tech.color} opacity-20 font-mono font-bold tracking-wider`}
            style={{
              top: `${(index * 8) + 5}%`,
              left: `${(index % 3) * 10 + 5}%`,
              fontSize: `${Math.random() * (1.5 - 1) + 1}rem`
            }}
          >
            {tech.name}
          </div>
        ))}
      </div>

      {/* Floating Animations */}
      <style jsx>{`
        @keyframes float1 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(20px, -20px) rotate(2deg); } }
        @keyframes float2 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(-25px, 25px) rotate(-2deg); } }
        @keyframes float3 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(30px, 30px) rotate(3deg); } }
        @keyframes float4 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(-20px, -30px) rotate(-3deg); } }
        @keyframes float5 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(25px, -25px) rotate(2deg); } }
        @keyframes float6 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(-30px, 20px) rotate(-2deg); } }
        @keyframes float7 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(20px, 30px) rotate(2deg); } }
        @keyframes float8 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(-25px, -20px) rotate(-3deg); } }
        .animate-float1 { animation: float1 8s ease-in-out infinite; }
        .animate-float2 { animation: float2 9s ease-in-out infinite; }
        .animate-float3 { animation: float3 10s ease-in-out infinite; }
        .animate-float4 { animation: float4 8.5s ease-in-out infinite; }
        .animate-float5 { animation: float5 9.5s ease-in-out infinite; }
        .animate-float6 { animation: float6 10.5s ease-in-out infinite; }
        .animate-float7 { animation: float7 11s ease-in-out infinite; }
        .animate-float8 { animation: float8 9.8s ease-in-out infinite; }
      `}</style>

      {/* Grid Background - Hidden on smallest screens */}
      <div className="absolute inset-0 bg-white bg-opacity-10 grid grid-cols-2 sm:grid-cols-4 grid-rows-4 gap-2 sm:gap-4 pointer-events-none">
        {Array(16).fill(null).map((_, index) => (
          <div key={index} className="border-r border-b sm:border-r-2 sm:border-b-2 border-white opacity-20"></div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 items-center justify-items-center gap-5 w-full py-10 md:py-0">
        {/* Image Section */}
        <div className="order-2 md:order-1 w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg md:border-r-2">
          <div className="relative bg-white shadow-xl px-1 py-1 sm:px-2 sm:py-2">
            <div className="flex justify-center items-center relative z-10 border border-gray-300 sm:border-2 overflow-hidden">
              {imageLoaded ? (
                <img
                  className="h-48 w-48 sm:h-64 sm:w-64 md:h-72 md:w-72 lg:h-80 lg:w-80 xl:h-96 xl:w-96 object-cover"
                  src={mypic}
                  alt="Profile"
                />
              ) : (
                <div className="h-48 w-48 sm:h-64 sm:w-64 md:h-72 md:w-72 lg:h-80 lg:w-80 xl:h-96 xl:w-96">
                  <Blurhash
                    hash={hashCode}
                    width="100%"
                    height="100%"
                    resolutionX={32}
                    resolutionY={32}
                    punch={1}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="order-1 md:order-2 flex flex-col justify-center text-center md:text-left text-white w-full">
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl">Hi Everyone</p>
          <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mt-2 sm:mt-4 font-bold">
            I'm <span className="text-orange-600">AMISHA KUMARI</span>.
          </p>
          <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl mt-2">
            I am a{" "}
            <span>
              <Typewriter
                words={["Full Stack Developer", "Web Designer", "Competitive Programmer"]}
                cursor
                cursorStyle="|"
                typeSpeed={50}
                deleteSpeed={50}
                delaySpeed={1000}
                loop={true}
              />
            </span>
          </p>
          <p className="mt-2 text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl max-w-md md:max-w-full mx-auto md:mx-0">
            Developer devoted to crafting beautiful web experiences
            focused on simplicity and purpose.
          </p>
          
          {/* Social Icons - Responsive spacing */}
          <div className="flex  md:justify-start space-x-4 sm:space-x-6 md:space-x-8 lg:space-x-10 p-4 md:p-6 lg:p-8 mt-4 md:mt-6">
            <IconWithTooltip 
              icon={<FaGithub className="text-gray-500 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 transition duration-300 hover:text-white" />}
              label="GitHub"
              href="https://github.com/AMISHA2004-devgeek"
            />
            <IconWithTooltip 
              icon={<FaLinkedin className="text-gray-500 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 transition duration-300 hover:text-white" />}
              label="LinkedIn"
              href="https://www.linkedin.com/in/amisha-kumari-351ab9274/"
            />
            <IconWithTooltip 
              icon={<FaEnvelope className="text-gray-500 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 transition duration-300 hover:text-white" />}
              label="Email"
              href="mailto:amishakumarijsr009@gmail.com"
            />
            <IconWithTooltip 
              icon={<FaCode className="text-gray-500 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 transition duration-300 hover:text-white" />}
              label="Codolio"
              href="https://codolio.com/profile/Amisha_11"
            />
          </div>
          
          {/* Download CV button - Centered on mobile, positioned on larger screens */}
          
        </div>
      </div>
    </section>
  );
};

export default HeroSection;